"use client";
import cx from "classnames";
import { RefObject, useState, useCallback } from "react";
import ReactModal from "react-modal";
import { useRedirectConfirmationModal } from "@/components/oddsBtn";
import { NFL } from "@/constants/sports";
import RedirectToSportsbook from "@/events/Modals/RedirectToSportsbookModal";
import { PopularBet } from "@/graphql/gql/graphql";
import { dummyParlay } from "@/utils/headToHead";
import { useHeadToHeadContext } from "@/utils/hooks/useHeadToHeadContext";
import ParlayContainer from "./ParlayContainer";
import styles from "./popularBets.module.scss";

interface PopularBetsDisplayProps {
  containerRef: RefObject<HTMLDivElement | null>;
  scrollUpdate: (() => void) & {
    clear(): void;
  } & {
    flush(): void;
  };
  scrollRef: RefObject<HTMLDivElement | null>;
  parlays: PopularBet[];
}

if (process.env.NODE_ENV !== "test") {
  ReactModal.setAppElement("#root");
}

export default function PopularBetsDisplay({
  containerRef,
  scrollRef,
  scrollUpdate,
  parlays,
}: PopularBetsDisplayProps) {
  const { eventName } = useHeadToHeadContext();
  const [isOpen, setIsOpen] = useState(false);
  const toggleModal = useCallback(() => setIsOpen(!isOpen), [isOpen]);
  const { onOddsClick, redirectToSportsbookProps } =
    useRedirectConfirmationModal(NFL, eventName, "popular_bets");

  // the modal renders the active parlay
  const [activeParlay, setActiveParlay] = useState<PopularBet>(parlays[0]);

  // show or hide the modal and set the active parlay
  const onClickParlayContainer = useCallback(
    (parlay: PopularBet) => {
      setActiveParlay(parlay);
      toggleModal();
    },
    [toggleModal]
  );

  const hasParlays = parlays.length > 0;

  const onKeyDownParlayContainer = (
    e: React.KeyboardEvent<HTMLDivElement>,
    parlay: PopularBet
  ) => {
    if (e.key === "Enter") {
      onClickParlayContainer(parlay);
    }
  };

  return (
    <>
      <RedirectToSportsbook {...redirectToSportsbookProps} />
      <ReactModal
        className={{
          base: styles.modal,
          afterOpen: styles.modalOpen,
          beforeClose: styles.modalClosed,
        }}
        overlayClassName={{
          base: styles.overlay,
          afterOpen: styles.overlayOpen,
          beforeClose: styles.overlayClosed,
        }}
        isOpen={isOpen}
        closeTimeoutMS={150}
        contentLabel="Parlay Details"
        onRequestClose={toggleModal}
        ariaHideApp={process.env.NODE_ENV !== "test"}
      >
        <ParlayContainer
          addToBetslip={onOddsClick}
          isModal
          toggleModal={toggleModal}
          parlay={activeParlay}
        />
      </ReactModal>
      <div className={styles.cardContainer}>
        <div
          className={cx(
            styles.cardContent,
            !hasParlays && styles.oddsUnavailable
          )}
          onScroll={scrollUpdate}
          ref={containerRef}
        >
          {hasParlays
            ? parlays.map((parlay) => {
                return (
                  <div
                    role="button"
                    key={`${parlay.betUrl}-${parlay.count}`}
                    tabIndex={0}
                    onClick={() => onClickParlayContainer(parlay)}
                    onKeyDown={(e) => onKeyDownParlayContainer(e, parlay)}
                  >
                    <ParlayContainer
                      addToBetslip={onOddsClick}
                      toggleModal={toggleModal}
                      parlay={parlay}
                    />
                  </div>
                );
              })
            : [null, null].map((_, index) => (
                <div key={index.toString()}>
                  <ParlayContainer
                    addToBetslip={() => null}
                    toggleModal={() => null}
                    parlay={dummyParlay}
                  />
                </div>
              ))}
          <div ref={scrollRef} />
        </div>
      </div>
    </>
  );
}
